import React from "react";
import { Button } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import type { InvoiceType } from "../../types/participantsTypes";
//import logo from "../../../../../public/report-logo-small.png";
import { amiriFontBase64 } from '../../../../common/fonts/Amiri-Regular';
import { useTranslation } from "react-i18next";
const logo = "/report-logo-small.png";
interface Props {
    invoice: InvoiceType;
}

const InvoicePrint: React.FC<Props> = ({ invoice }) => {

    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";


    const handlePrint = async (invoice: InvoiceType) => {
        try {
            const doc = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
                precision: 2, // ✅ reduce internal float precision
                compress: true, // ✅ enable built-in compression
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            const drawLabelValue = (label: string, value: string, x: number, y: number) => {
                if (isArabic) {
                    doc.text(`${value} :${label}`, x, y, { align: "right" });
                } else {
                    doc.text(`${label}: ${value}`, x, y, { align: "left" });
                }
            };

            // === Add Arabic font ===
            if (amiriFontBase64) {
                doc.addFileToVFS("Amiri-Regular.ttf", amiriFontBase64);
                doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
            }

            const fontName = amiriFontBase64 ? "Amiri" : "helvetica";


            // === Load and compress logo (convert to JPEG) ===
            const imageData = await fetch(logo)
                .then((r) => r.blob())
                .then(
                    (blob) =>
                        new Promise<string>((resolve) => {
                            const reader = new FileReader();
                            const img = new Image();
                            reader.onload = () => {
                                img.src = reader.result as string;
                                img.onload = () => {
                                    const canvas = document.createElement("canvas");
                                    const ctx = canvas.getContext("2d")!;
                                    const targetWidth = img.width * 0.5; // reduce by 50%
                                    const targetHeight = img.height * 0.5;
                                    canvas.width = targetWidth;
                                    canvas.height = targetHeight;
                                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                                    // ✅ convert to JPEG with slight compression
                                    resolve(canvas.toDataURL("image/jpeg", 0.8));
                                };
                            };
                            reader.readAsDataURL(blob);
                        })
                );

            // === Watermark ===
            const addBackgroundLogo = () => {
                const logoWidth = 80;
                const logoHeight = 80;
                const logoX = (pageWidth - logoWidth) / 2;
                const logoY = (pageHeight - logoHeight) / 2;

                doc.saveGraphicsState();
                doc.setGState(new (doc as any).GState({ opacity: 0.08 }));
                doc.addImage(imageData, "JPEG", logoX, logoY, logoWidth, logoHeight); // ✅ JPEG instead of PNG
                doc.restoreGraphicsState();
            };


            // === Header Logo ===
            const addHeaderLogo = () => {
                const logoSize = 14;
                const y = 20;

                if (isArabic) {
                    // Move logo to right side for Arabic layout
                    const x = pageWidth - logoSize - 14;
                    doc.addImage(imageData, "JPEG", x, y, logoSize, logoSize);
                } else {
                    // Default: English layout (logo on left)
                    doc.addImage(imageData, "JPEG", 14, y, logoSize, logoSize);
                }
            };

            // === Header info ===
            doc.setFont(fontName, "normal");
            doc.setFontSize(22);
            doc.setTextColor(20, 20, 20);
            const headerText = `${t("Invoice")} #${invoice.id}`;
            const headerX = isArabic ? pageWidth - 34 : 34;
            doc.text(headerText, headerX, 27, { align: isArabic ? "right" : "left" });

            doc.setFontSize(12);
            doc.setFont(fontName, "normal");
            const nameX = isArabic ? pageWidth - 34 : 34;
            doc.text(invoice.name, nameX, 33, { align: isArabic ? "right" : "left" });

            // === Status Box ===
            const status = (invoice.status ?? "UNPAID").toUpperCase();
            const stampColor: [number, number, number] =
                status === "PAID"
                    ? [0, 128, 0]
                    : status === "CANCELLED"
                        ? [255, 0, 0]
                        : [255, 0, 0];

            const stampFontSize = 16;
            doc.setFontSize(stampFontSize);
            doc.setFont(fontName, "normal");
            doc.setTextColor(...stampColor);

            const textWidth = doc.getTextWidth(status);
            const margin = 15;
            const paddingX = 6;
            const paddingY = 4;
            const textHeight = stampFontSize * 0.5;

            let rectX, textX;

            // === Flip side depending on language ===
            if (isArabic) {
                // Move stamp to LEFT side for Arabic layout
                rectX = margin;
                textX = rectX + (textWidth + paddingX * 2) / 2;
            } else {
                // Default (English): stamp on RIGHT
                rectX = pageWidth - margin - (textWidth + paddingX * 2);
                textX = rectX + (textWidth + paddingX * 2) / 2;
            }

            // === Draw stamp ===
            doc.text(status, textX, 25, { align: "center" });
            doc.setDrawColor(...stampColor);
            doc.setLineWidth(1);
            (doc as any).setLineDashPattern([2, 2], 0);
            doc.rect(
                rectX,
                25 - textHeight - paddingY / 2,
                textWidth + paddingX * 2,
                textHeight + paddingY
            );
            doc.setTextColor(0, 0, 0);
            (doc as any).setLineDashPattern([], 0);
            // === Line ===
            doc.setLineWidth(0.4);
            doc.setDrawColor(200, 200, 200);
            doc.line(14, 38, 196, 38);

            // === Invoice Info ===
            const leftX = isArabic ? pageWidth - 14 : 14;
            const rightX = isArabic ? pageWidth - 105 : 105;
            let y = 47;
            doc.setFontSize(10);
            drawLabelValue(t("Plan"), invoice.planName ?? "", leftX, y);

            //drawLabelValue(t("Group"), invoice.groupName ?? "", rightX, y);
            drawLabelValue(t("Currency"), invoice.currencyISOCode ?? "", rightX, y);

            y += 8;
            drawLabelValue(t("InvoiceDate"), dayjs(invoice.invoiceDate).format("DD-MM-YYYY"), leftX, y);
            drawLabelValue(t("DueDate"), dayjs(invoice.dueDate).format("DD-MM-YYYY"), rightX, y);

            // === Table ===
            const tableHead = isArabic
                ? [[t("Amount"), t("Category"), t("Gender"), t("Age"), t("Name")]] // Arabic: reverse
                : [["Name", "Age", "Gender", "Category", "Amount"]];

            const tableBody = invoice.lineItems?.map((item) => {
                const row = [
                    item.name ?? "",
                    item.age ?? "",
                    item.gender ?? "",
                    item.category ?? "",
                    `${item.amount?.toLocaleString() ?? "0"} ${item.currencyISOCode ?? ""}`,
                ];
                return isArabic ? row.reverse() : row; // flip row order for Arabic
            }) ?? [];

            const tableFoot = isArabic
                ? [[`${invoice.totalAmount.toLocaleString()} ${invoice.currencyISOCode}`, t("Total"), "", "", ""]]
                : [["", "", "", "Total", `${invoice.totalAmount.toLocaleString()} ${invoice.currencyISOCode}`]];

            autoTable(doc, {
                startY: y + 15,
                head: tableHead,
                body: tableBody,
                foot: tableFoot,
                styles: {
                    font: fontName,
                    fontSize: 10,
                    halign: isArabic ? "right" : "left",
                    valign: "middle",
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: "normal",
                },
                footStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: "normal",
                },
                tableLineWidth: 0.1,
                tableLineColor: [200, 200, 200],
                theme: "grid",
                tableWidth: "auto", // ✅ make table full width automatically
                margin: { left: 14, right: 14 }, // table respects page margins
            });

            const finalY = (doc as any).lastAutoTable.finalY + 10;
            if (invoice.notes) {
                doc.setFont(fontName, "normal");
                drawLabelValue(t("Note"), invoice.notes, leftX, finalY);
            }


            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                addBackgroundLogo();
                addHeaderLogo();

                doc.setDrawColor(180, 180, 180);
                doc.setLineWidth(0.3);
                doc.line(14, pageHeight - 18, pageWidth - 14, pageHeight - 18);

                doc.setFontSize(9);
                doc.setFont(fontName, "italic");
                doc.setTextColor(100);

                doc.text("TPA Insurance Company Ltd.", 14, pageHeight - 14);
                doc.text("123 Business Street, Iraq | +964 780-666-9919", 14, pageHeight - 10);
                doc.text(
                    "https://insurance-staging.altaifgroup.com/",
                    pageWidth - 14,
                    pageHeight - 10,
                    { align: "right" }
                );
            }

            // === Save PDF ===
            doc.save(`Invoice-${invoice.id}.pdf`);
        } catch (err) {
            console.error("Error generating PDF:", err);
        }
    };

    return (
        <Button
            size="large"
            style={{ cursor: "pointer", color: "red" }}
            icon={<FilePdfOutlined />}
            onClick={() => handlePrint(invoice)}
        />
    );
};

export default InvoicePrint;
